const elementName = 'aceii-hardware-specs-tabs';
const headerSuppressionClass = 'aceii-hardware-sticky-nav-active';
const activeStickyNavInstances = new Set();

function syncHeaderSuppressionState() {
  document.documentElement.classList.toggle(
    headerSuppressionClass,
    activeStickyNavInstances.size > 0,
  );
}

if (
  typeof window !== 'undefined'
  && typeof HTMLElement !== 'undefined'
  && !window.customElements.get(elementName)
) {
  class AceiiHardwareSpecsTabs extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.tabButtons = [...this.querySelectorAll('[data-hardware-tab]')];
      this.cardEls = [...this.querySelectorAll('[data-hardware-card]')];
      this.descriptionEl = this.querySelector('[data-hardware-description]');
      this.tabsScroller = this.querySelector('.aceii-hardware-specs__tabs');
      this.navShell = this.querySelector('[data-hardware-nav]');
      this.navSentinel = this.querySelector('.aceii-hardware-specs__nav-sentinel');
      this.cardGrid = this.querySelector('.aceii-hardware-specs__card-grid');
      this.cursorHint = this.querySelector('[data-hardware-cursor]');
      this.sectionVisible = false;
      this.autoRotateVisible = false;
      this.documentVisible = !document.hidden;
      this.autoRotateMs = Number(this.dataset.autoRotateMs) || 15000;
      this.activeKey = this.tabButtons[0]?.dataset.hardwareTab ?? '';
      this.mobileMedia = window.matchMedia('(max-width: 749px)').matches;
      this.activeVideos = [];
      this.videoPreloadCache = new Map();
      this.autoTimer = null;

      try {
        this.tabsData = JSON.parse(
          this.querySelector('[data-hardware-tabs-json]')?.textContent ?? '[]',
        );
      } catch {
        this.tabsData = [];
      }

      this.tabButtons.forEach((button) => {
        button.addEventListener(
          'click',
          () => this.activate(button.dataset.hardwareTab, { userInitiated: true }),
          { signal },
        );
        button.addEventListener(
          'pointerenter',
          () => this.preloadTabVideos(button.dataset.hardwareTab, 'metadata'),
          { signal },
        );
        button.addEventListener(
          'focus',
          () => this.preloadTabVideos(button.dataset.hardwareTab, 'metadata'),
          { signal },
        );
      });

      document.addEventListener(
        'visibilitychange',
        () => {
          this.documentVisible = !document.hidden;
          this.syncVideoPlayback();
          if (this.documentVisible && this.autoRotateVisible) {
            this.restartAutoRotate();
          } else {
            this.clearAutoTimer();
          }
        },
        { signal },
      );

      this.sectionObserver = new IntersectionObserver(
        ([entry]) => {
          this.sectionVisible = Boolean(entry?.isIntersecting);
          if (this.sectionVisible) {
            this.preloadAdjacentTabs();
          }
          this.syncVideoPlayback();
        },
        {
          rootMargin: '420px 0px',
          threshold: 0.01,
        },
      );
      this.sectionObserver.observe(this);

      this.autoRotateObserver = new IntersectionObserver(
        ([entry]) => {
          const wasVisible = this.autoRotateVisible;
          this.autoRotateVisible = Boolean(entry?.isIntersecting);

          if (this.autoRotateVisible && !wasVisible && this.documentVisible) {
            this.restartAutoRotate();
          } else if (!this.autoRotateVisible) {
            this.clearAutoTimer();
          }
        },
        {
          rootMargin: '0px',
          threshold: 0.6,
        },
      );
      this.autoRotateTarget = this.navShell || this.cardGrid || this;
      this.autoRotateObserver.observe(this.autoRotateTarget);

      this.updateStickyState = this.updateStickyState.bind(this);
      window.addEventListener('scroll', this.updateStickyState, { passive: true, signal });
      window.addEventListener('resize', this.updateStickyState, { passive: true, signal });
      window.addEventListener(
        'resize',
        () => {
          const nextMobileMedia = window.matchMedia('(max-width: 749px)').matches;
          if (nextMobileMedia === this.mobileMedia) return;
          this.mobileMedia = nextMobileMedia;
          this.activate(this.activeKey, { userInitiated: false });
        },
        { passive: true, signal },
      );
      this.updateStickyState();

      if (this.cardGrid && this.cursorHint) {
        this.cardGrid.addEventListener(
          'pointermove',
          (event) => this.updateCursorHint(event),
          { signal },
        );
        this.cardGrid.addEventListener(
          'pointerleave',
          () => this.hideCursorHint(),
          { signal },
        );
      }

      if (this.cardGrid) {
        this.cardGrid.addEventListener(
          'click',
          (event) => this.handleCardGridClick(event),
          { signal },
        );
      }

      if (this.tabsScroller) {
        this.tabsScroller.addEventListener(
          'pointerdown',
          () => this.clearAutoTimer(),
          { signal },
        );
        const resumeAutoRotate = () => {
          if (this.autoRotateVisible && this.documentVisible) {
            this.restartAutoRotate();
          }
        };
        this.tabsScroller.addEventListener('pointerup', resumeAutoRotate, { signal });
        this.tabsScroller.addEventListener('pointercancel', resumeAutoRotate, { signal });
      }

      this.activate(this.activeKey, { userInitiated: false });
    }

    disconnectedCallback() {
      this.clearAutoTimer();
      this.pauseVideos();
      this.setHeaderSuppression(false);
      this.sectionObserver?.disconnect();
      this.autoRotateObserver?.disconnect();
      this.abortController?.abort();
      this.abortController = null;
    }

    getTabRecord(key) {
      return this.tabsData.find((tab) => tab.key === key) ?? this.tabsData[0];
    }

    activate(key, { userInitiated }) {
      const record = this.getTabRecord(key);
      if (!record) return;

      this.activeKey = record.key;
      this.descriptionEl.textContent = record.headerDescription ?? '';

      this.tabButtons.forEach((button) => {
        const active = button.dataset.hardwareTab === record.key;
        button.classList.toggle('is-active', active);
        button.classList.toggle('is-progressing', false);
        button.setAttribute('aria-selected', String(active));
      });

      this.cardEls.forEach((card) => {
        card.classList.remove('is-changing');
      });
      void this.offsetWidth;

      this.activeVideos = [];
      this.cardEls.forEach((card, index) => {
        const cardData = record.cards?.[index] ?? {};
        this.renderCard(card, cardData);
        card.classList.add('is-changing');
      });

      const activeButton = this.tabButtons.find((button) => button.dataset.hardwareTab === record.key);
      if (activeButton) {
        this.centerActiveTab(activeButton);
      }

      if (userInitiated) {
        this.restartAutoRotate();
      } else {
        this.scheduleNext();
      }

      this.syncVideoPlayback();
      this.preloadAdjacentTabs();
    }

    renderCard(card, cardData) {
      const imageEl = card.querySelector('[data-card-image]');
      const videoTemplate = card.querySelector('[data-card-video]');
      const titleEl = card.querySelector('[data-card-title]');
      const textEl = card.querySelector('[data-card-text]');

      const videoSrc = this.getCardVideoSource(cardData);
      const posterSrc = this.getCardPosterSource(cardData);
      const hasVideo = Boolean(videoSrc && videoTemplate);
      card.classList.toggle('is-video', hasVideo);
      card.classList.toggle('is-video-loading', false);
      card.classList.toggle('is-video-ready', false);
      if (hasVideo) {
        card.dataset.activeVideoSrc = videoSrc;
      } else {
        delete card.dataset.activeVideoSrc;
      }

      titleEl.textContent = cardData.title ?? '';
      textEl.textContent = cardData.text ?? '';

      imageEl.style.removeProperty('--ahs-card-media');
      imageEl.style.removeProperty('--ahs-card-media-mobile');
      if (cardData.image) {
        imageEl.style.setProperty('--ahs-card-media', `url('${cardData.image}')`);
      }
      if (cardData.mobileImage) {
        imageEl.style.setProperty('--ahs-card-media-mobile', `url('${cardData.mobileImage}')`);
      }

      card.querySelectorAll('[data-card-video]').forEach((video) => {
        video.classList.remove('is-active');
        if (!video.paused) video.pause();
      });

      if (hasVideo) {
        const videoEl = this.getPersistentVideo(card, videoTemplate, videoSrc);
        videoEl.dataset.src = videoSrc;
        if (posterSrc) {
          videoEl.setAttribute('poster', posterSrc);
        } else {
          videoEl.removeAttribute('poster');
        }
        videoEl.classList.add('is-active');
        card.classList.toggle(
          'is-video-ready',
          videoEl.readyState >= 2 && Boolean(videoEl.currentSrc || videoEl.src),
        );
        card.classList.toggle('is-video-loading', !card.classList.contains('is-video-ready'));

        this.bindVideoReadyState(card, videoEl, videoSrc);
        this.activeVideos.push(videoEl);

        if (this.sectionVisible && this.documentVisible) {
          this.prepareVideo(videoEl, videoSrc, 'auto');
        }
      }

    }

    getCardVideoSource(cardData) {
      if (!cardData) return '';
      return this.mobileMedia
        ? cardData.mobileVideo || cardData.video || ''
        : cardData.video || cardData.mobileVideo || '';
    }

    getCardPosterSource(cardData) {
      if (!cardData) return '';
      return this.mobileMedia
        ? cardData.mobileImage || cardData.image || ''
        : cardData.image || cardData.mobileImage || '';
    }

    getPersistentVideo(card, templateVideo, src) {
      if (!card._aceiiVideoBySource) {
        card._aceiiVideoBySource = new Map();
      }

      const cachedVideo = card._aceiiVideoBySource.get(src);
      if (cachedVideo) return cachedVideo;

      const existingVideo = [...card.querySelectorAll('[data-card-video]')]
        .find((video) => video.dataset.src === src);
      const shouldReuseTemplate = card._aceiiVideoBySource.size === 0;
      const video = existingVideo ?? (shouldReuseTemplate ? templateVideo : templateVideo.cloneNode(false));

      video.className = templateVideo.className;
      video.classList.remove('is-active');
      video.dataset.src = src;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = video.preload || 'metadata';

      if (video !== templateVideo && !existingVideo) {
        video.removeAttribute('src');
      }

      if (!video.isConnected) {
        templateVideo.parentElement?.append(video);
      }

      card._aceiiVideoBySource.set(src, video);

      return video;
    }

    syncVideoPlayback() {
      const shouldPlay = this.sectionVisible && this.documentVisible;

      this.activeVideos.forEach((video) => {
        if (!video.dataset.src) return;

        if (!shouldPlay) {
          if (!video.paused) video.pause();
          return;
        }

        this.prepareVideo(video, video.dataset.src, 'auto');

        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            video.pause();
          });
        }
      });
    }

    bindVideoReadyState(card, video, src) {
      const isCurrentVideo = () => (
        card.dataset.activeVideoSrc === src
        && video.dataset.src === src
        && video.classList.contains('is-active')
      );

      const markReady = () => {
        video._aceiiReadyStatePending = false;
        if (!isCurrentVideo()) return;

        card.classList.add('is-video-ready');
        card.classList.remove('is-video-loading');
      };

      const markUnavailable = () => {
        video._aceiiReadyStatePending = false;
        if (!isCurrentVideo()) return;

        card.classList.remove('is-video-loading');
      };

      if (video.readyState >= 2 && (video.currentSrc || video.src)) {
        markReady();
        return;
      }

      if (video._aceiiReadyStatePending) return;

      video._aceiiReadyStatePending = true;
      video.addEventListener('loadeddata', markReady, { once: true });
      video.addEventListener('canplay', markReady, { once: true });
      video.addEventListener('error', markUnavailable, { once: true });
    }

    prepareVideo(video, src, preload = 'metadata') {
      if (!video || !src) return;

      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = preload;

      if (video.currentSrc === src || video.getAttribute('src') === src) return;

      video.src = src;
      video.load();
    }

    preloadVideoSource(src, preload = 'metadata') {
      if (!src) return;

      if (this.videoPreloadCache.has(src)) {
        const cachedVideo = this.videoPreloadCache.get(src);
        if (preload === 'auto') {
          cachedVideo.preload = 'auto';
          if (cachedVideo.readyState === 0) {
            cachedVideo.load();
          }
        }
        return;
      }

      const video = document.createElement('video');
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = preload;
      video.src = src;
      video.load();

      this.videoPreloadCache.set(src, video);
    }

    preloadTabVideos(key, preload = 'metadata') {
      if (!key) return;

      const record = this.getTabRecord(key);
      if (!record) return;

      record.cards?.forEach((card) => {
        const videoSrc = this.getCardVideoSource(card);
        if (videoSrc) {
          this.preloadVideoSource(videoSrc, preload);
        }
      });
    }

    preloadAdjacentTabs() {
      if (!this.sectionVisible || !this.tabsData.length) return;

      const activeIndex = this.tabsData.findIndex((tab) => tab.key === this.activeKey);
      if (activeIndex < 0) return;

      this.preloadTabVideos(this.activeKey, 'auto');

      if (this.tabsData.length < 2) return;

      const previousIndex = (activeIndex - 1 + this.tabsData.length) % this.tabsData.length;
      const nextIndex = (activeIndex + 1) % this.tabsData.length;

      this.preloadTabVideos(this.tabsData[previousIndex]?.key, 'metadata');
      this.preloadTabVideos(this.tabsData[nextIndex]?.key, 'metadata');
    }

    pauseVideos() {
      this.querySelectorAll('[data-card-video]').forEach((video) => {
        if (!video.paused) video.pause();
      });
    }

    restartAutoRotate() {
      this.clearAutoTimer();
      this.scheduleNext();
    }

    scheduleNext() {
      this.clearAutoTimer();
      if (
        this.tabButtons.length < 2
        || !this.autoRotateVisible
        || !this.documentVisible
      ) return;

      const activeButton = this.tabButtons.find(
        (button) => button.dataset.hardwareTab === this.activeKey,
      );
      if (activeButton) {
        void activeButton.offsetWidth;
        activeButton.classList.add('is-progressing');
      }

      this.autoTimer = window.setTimeout(() => {
        const currentIndex = this.tabButtons.findIndex(
          (button) => button.dataset.hardwareTab === this.activeKey,
        );
        const nextIndex = currentIndex >= 0
          ? (currentIndex + 1) % this.tabButtons.length
          : 0;
        this.activate(this.tabButtons[nextIndex].dataset.hardwareTab, { userInitiated: false });
      }, this.autoRotateMs);
    }

    clearAutoTimer() {
      if (this.autoTimer) {
        window.clearTimeout(this.autoTimer);
        this.autoTimer = null;
      }

      this.tabButtons?.forEach((button) => {
        button.classList.remove('is-progressing');
      });
    }

    centerActiveTab(button) {
      if (!button || !this.tabsScroller || window.innerWidth > 820) return;

      const scrollerRect = this.tabsScroller.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const targetLeft = this.tabsScroller.scrollLeft
        + buttonRect.left
        - scrollerRect.left
        - ((scrollerRect.width - buttonRect.width) / 2);
      const maxScrollLeft = Math.max(
        0,
        this.tabsScroller.scrollWidth - this.tabsScroller.clientWidth,
      );
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.tabsScroller.scrollTo({
        left: Math.min(Math.max(targetLeft, 0), maxScrollLeft),
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    }

    updateStickyState() {
      if (!this.navShell || !this.navSentinel) {
        this.setHeaderSuppression(false);
        return;
      }

      const sentinelRect = this.navSentinel.getBoundingClientRect();
      const navRect = this.navShell.getBoundingClientRect();
      const sectionRect = this.getBoundingClientRect();
      const stickyTop = Number.parseFloat(
        window.getComputedStyle(document.documentElement)
          .getPropertyValue('--aceii-product-anchor-nav-height'),
      ) || 0;
      const isStuck = (
        sentinelRect.top <= stickyTop
        && navRect.top <= stickyTop + 1
        && navRect.bottom > 0
        && sectionRect.bottom > navRect.height
      );

      this.navShell.classList.toggle('is-stuck', isStuck);
      this.setHeaderSuppression(isStuck);
    }

    setHeaderSuppression(active) {
      if (active) {
        activeStickyNavInstances.add(this);
      } else {
        activeStickyNavInstances.delete(this);
      }

      syncHeaderSuppressionState();
    }

    updateCursorHint(event) {
      if (!this.cursorHint || window.innerWidth <= 820) return;

      const gridRect = this.cardGrid.getBoundingClientRect();
      const isRight = event.clientX > (gridRect.left + (gridRect.width / 2));
      this.cursorHint.style.left = `${event.clientX}px`;
      this.cursorHint.style.top = `${event.clientY}px`;
      this.cursorHint.classList.add('is-visible');
      this.cursorHint.classList.toggle('is-right', isRight);
    }

    handleCardGridClick(event) {
      if (
        !this.cardGrid
        || this.tabButtons.length < 2
        || window.matchMedia('(max-width: 820px)').matches
      ) return;

      const gridRect = this.cardGrid.getBoundingClientRect();
      const currentIndex = this.tabButtons.findIndex(
        (button) => button.dataset.hardwareTab === this.activeKey,
      );

      if (currentIndex < 0) return;

      const moveForward = event.clientX > (gridRect.left + (gridRect.width / 2));
      const nextIndex = moveForward
        ? (currentIndex + 1) % this.tabButtons.length
        : (currentIndex - 1 + this.tabButtons.length) % this.tabButtons.length;

      this.activate(this.tabButtons[nextIndex].dataset.hardwareTab, { userInitiated: true });
    }

    hideCursorHint() {
      this.cursorHint?.classList.remove('is-visible', 'is-right');
    }

    escapeHtml(value) {
      return value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
    }
  }

  window.customElements.define(elementName, AceiiHardwareSpecsTabs);
}
